/**
 * Вкладка «Продажи» «Голоса клиента» слоями (#2060, спека
 * docs/plans/2026-10-11-voice-report-layers.md, PR 1 §9): одна полоса
 * фильтра на вкладку с меню выгрузки (возражения, отказы и вопросы — #2315,
 * #2350), сводка из 3–4 карточек, переключатель разделов —
 * Возражения | Отказы | Вопросы | Темы, — и только открытый раздел целиком.
 *
 * Новых запросов нет: сводке нужны все три ответа вкладки — поток
 * возражений, `only=customerQuestions` и `only=themes&themeCuts=outcomes`, — и
 * вкладка отдаёт разделам те же запросы, а не ключи: раздел, открытый позже,
 * не заводит своего окна периода и своего перечитывания. Раздел — в адресе
 * (`voiceSection`), как вкладка: «Назад» из разговора возвращает в тот же
 * раздел.
 *
 * Разделы «Отказы» и «Вопросы» и их карточки — за флагом вопросов клиентов
 * (`enableCustomerQuestions`), «Темы» — за флагом тем. Закрыто на клиенте или
 * в ответе сервера — нет ни раздела, ни карточки; набор тем пуст — тоже (#2072:
 * «Задать темы» — кнопка в шапке раздела). Отказ раздела сервером (403
 * с кодом: тариф сменился под открытым экраном) закрывает вкладку целиком
 * словами `VoiceLocked`: «Продажи» внутри раздела закрыты вместе с ним.
 *
 * Фильтр темы (#2229, §2.5): поток возражений и блоки вопросов и отказов
 * сужены темой, строка фильтра над сводкой берёт числа из эха ответа вопросов
 * (вопросы закрыты — из эха потока). Ответ без эха выбранной темы — чисел нет.
 * Блок тем фильтром не сужен; карточка темы называет строку выбранной темы, а
 * ответ без эха прячет суженные блоки, но не его (#2238).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateShort } from '@/lib/utils';
import { TabsContent } from '@/components/ui/tabs';
import { ObjectionsList } from '@/components/analytics/objections/ObjectionsList';
import { useObjectionStream } from '@/components/analytics/objections/useObjectionStream';
import {
  objectionPrintNotes,
  objectionSection,
} from '@/components/analytics/objections/objection-view-model';
import { StreamFilterBar } from '../stream-filter/StreamFilterBar';
import { VoiceChangesSection } from './VoiceChangesSection';
import { VoiceExportMenu } from './VoiceExportMenu';
import { VoiceLayers, type VoiceLayerCard } from './VoiceLayers';
import { VoiceLoadError } from './VoiceLoadError';
import { VoiceLoading } from './VoiceLoading';
import { VoiceLocked } from './VoiceLocked';
import { tierRefusalCode } from '@/lib/tier-refusal';
import { VoiceSalesDeclines } from './VoiceSalesDeclines';
import { VoiceSalesQuestions } from './VoiceSalesQuestions';
import type { QuestionsSwitch } from './VoiceSiteTab';
import { VoiceThemeFilterBlocks } from './VoiceThemeFilterBlocks';
import { VoiceThemeFilterStrip } from './VoiceThemeFilterStrip';
import { VoiceThemesBlock } from './VoiceThemesBlock';
import { useVoiceThemeFilterTab, voiceThemeFilterTheme } from './useVoiceThemeFilter';
import { VOICE_TOP_ROWS } from './VoiceTopList';
import { useVoiceCustomerQuestions } from './useVoiceOfCustomer';
import { useVoiceThemesLayer } from './useVoiceThemesLayer';
import { tabSeriesWindow } from './useVoiceSeries';
import { changesBlock } from './voice-series-view-model';
import {
  themeChangeSection,
  themeExportSection,
  themePrintNotes,
} from './voice-themes-view-model';
import {
  customerQuestionsState,
  declineSection,
  questionSection,
  questionsPrintNotes,
  salesQuestionSections,
  salesQuestionsDenominatorNote,
} from './voice-questions-view-model';
import {
  VOICE_SALES_SECTIONS,
  type VoiceSalesSection,
  type VoiceSearch,
} from './voice-search';
import {
  declineLead,
  objectionLead,
  summaryCardState,
  themeLead,
  unansweredLead,
} from './voice-summary-view-model';

interface VoiceSalesTabProps {
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  onOpenCall: (recordId: string) => void;
  questionsSwitch: QuestionsSwitch;
  /** Open the theme set's panel («Темы») — from the themes section. */
  onOpenThemes: () => void;
  upgradeHref?: string;
}

export function VoiceSalesTab({
  search,
  onSearchChange,
  onOpenCall,
  questionsSwitch,
  onOpenThemes,
  upgradeHref,
}: VoiceSalesTabProps) {
  const { t } = useTranslation('dashboard');
  // An unknown switch asks the server, which answers without blocks when closed.
  const questionsOn = questionsSwitch === 'on' || questionsSwitch === 'unknown';
  const { query: objections, streamWindow } = useObjectionStream(
    search,
    onSearchChange,
    true
  );
  const { query: questions } = useVoiceCustomerQuestions(search, onSearchChange, {
    enabled: questionsOn,
  });
  const themesLayer = useVoiceThemesLayer(search, onSearchChange, 'outcomes', (ready) =>
    themeLead(ready, t, search.voiceTheme)
  );
  // The questions' echo first: the strip's numbers are the questions' scope.
  const filter = useVoiceThemeFilterTab(
    search,
    voiceThemeFilterTheme(search, themesLayer),
    [...(questionsOn ? [questions] : []), objections]
  );
  const { query: themes, available: themesAvailable, shown: themesShown } = themesLayer;

  const refusal =
    (questionsOn && questions.isError ? tierRefusalCode(questions.error) : null) ??
    (themesAvailable && themes.isError ? tierRefusalCode(themes.error) : null);
  if (refusal) return <VoiceLocked code={refusal} upgradeHref={upgradeHref} />;
  // The theme is being lifted after a refusal: the page reloads without it.
  if (filter.refused) return <VoiceLoading />;

  const cqState = questions.data ? customerQuestionsState(questions.data) : null;
  const cq = cqState?.kind === 'ready' ? cqState.cq : undefined;
  const questionsShown = questionsOn && cqState?.kind !== 'closed';

  // «What changed» reads every block of the tab, the objection stream among
  // them (#2552) — the tab always has it, so the section is always there.
  const sections = VOICE_SALES_SECTIONS.filter((id) =>
    id === 'objections' || id === 'changes'
      ? true
      : id === 'themes'
        ? themesShown
        : questionsShown
  );
  const section = sections.find((id) => id === search.voiceSection) ?? 'objections';
  const open = (id: VoiceSalesSection) =>
    onSearchChange({ voiceSection: id === 'objections' ? undefined : id });

  const questionsCard = (lead: typeof declineLead) =>
    summaryCardState(questions, () => (cq ? lead(cq, t) : undefined));
  const cards: VoiceLayerCard<VoiceSalesSection>[] = [
    {
      id: 'objections',
      title: t('voicePage.layers.summary.objection'),
      state: summaryCardState(objections, (data) => objectionLead(data, t)),
    },
    ...(questionsShown
      ? [
          {
            id: 'declines' as const,
            title: t('voicePage.layers.summary.decline'),
            state: questionsCard(declineLead),
          },
          {
            id: 'questions' as const,
            title: t('voicePage.layers.summary.unanswered'),
            state: questionsCard(unansweredLead),
          },
        ]
      : []),
    ...(themesLayer.cardShown
      ? [
          {
            id: 'themes' as const,
            title: t('voicePage.layers.summary.theme'),
            state: themesLayer.card,
          },
        ]
      : []),
  ];

  const questionsBody = (render: (ready: NonNullable<typeof cq>) => ReactNode) => {
    if (questions.isPending) return <VoiceLoading />;
    // A failed refetch keeps the previous answer: not drawn as current.
    if (!cq || questions.isError) {
      return <VoiceLoadError onRetry={() => void questions.refetch()} />;
    }
    return render(cq);
  };
  // «What changed» (#2394 §9 п. 7): the marked rows of the tab's own answers.
  const changesBody = () => (
    <VoiceChangesSection
      blocks={[
        changesBlock(objections, objections.data, (ready) => [objectionSection(ready)]),
        ...(questionsShown
          ? [
              changesBlock(questions, cq, (ready) => [
                declineSection(ready),
                questionSection(ready),
              ]),
            ]
          : []),
        ...(themesShown
          ? [
              changesBlock(themes, themesLayer.ready, (ready) => [
                themeChangeSection(ready),
              ]),
            ]
          : []),
      ]}
      expandedRow={search.row}
      onToggleRow={(rowId) =>
        onSearchChange({ row: search.row === rowId ? undefined : rowId })
      }
      onOpenCall={onOpenCall}
      // A code's line is a share of every analysed conversation, its mark — of
      // those with a recognized objection: said once the view shows a code. A
      // failed refetch keeps the old answer, but its block is drawn as failed.
      notes={
        !objections.isError && objections.data?.codes.some((code) => code.mark)
          ? [t('voicePage.series.objectionsBase')]
          : []
      }
      sourceSection={(block) =>
        block === 'declines' || block === 'questions' || block === 'themes'
          ? block
          : undefined
      }
    />
  );
  const fetching = [
    objections,
    ...(questionsOn ? [questions] : []),
    ...(themesAvailable ? [themes] : []),
  ].some((query) => query.isFetching && !query.isLoading);
  const sectionProps = { search, onSearchChange, onOpenCall };
  // Not narrowed by the filter: still drawn when the narrowed blocks are not.
  const themesBlock = themesShown ? (
    <VoiceThemesBlock
      query={themes}
      {...sectionProps}
      onOpenThemes={onOpenThemes}
      limit={VOICE_TOP_ROWS}
      upgradeHref={upgradeHref}
    />
  ) : null;

  // The tab's export is its objections — the market view: the team view names
  // employees (#2350) — then its declines and questions blocks while the tab
  // shows them (#2315). A block the screen draws as failed or still loading
  // holds the menu back: the file must not drop it silently.
  // A switch turned off keeps the cached answer: only blocks the tab shows leave.
  const exportedCq = questionsShown ? cq : undefined;
  // The themes block leaves with them (#2009, `useVoiceThemesLayer`).
  const exportedThemes = themesLayer.exported.themes;
  const exportMenu =
    // A failed refetch keeps an answer the screen no longer shows.
    objections.isError ||
    (questionsShown && (questions.isError || cqState?.kind === 'failed')) ||
    themesLayer.exported.failed ? (
      <VoiceExportMenu unavailable="failed" />
    ) : objections.data &&
      (!questionsShown || cq) &&
      themesLayer.exported.settled &&
      filter.blocksShown ? (
      <VoiceExportMenu
        audience="sales"
        sections={[
          objectionSection(objections.data),
          ...(exportedCq ? salesQuestionSections(exportedCq) : []),
          ...(exportedThemes ? [themeExportSection(exportedThemes)] : []),
        ]}
        data={{
          kind: 'questions',
          periodStart: objections.data.periodStart,
          periodEnd: objections.data.periodEnd,
          // Every block carries its own cap (`VoiceSection.cap`).
          truncated: false,
          prevTruncated: false,
        }}
        // The notes of the blocks the report holds: objections, then declines
        // and questions, no first minute, then themes.
        printNotes={[
          ...objectionPrintNotes(objections.data, t, filter.comparable),
          ...(exportedCq
            ? questionsPrintNotes(exportedCq, t, formatDateShort, {
                comparable: filter.comparable,
                capsInBlocks: true,
                notes: [
                  t('voicePage.declines.denominatorNote'),
                  t(salesQuestionsDenominatorNote),
                  t('voicePage.questions.wordingNote'),
                ],
              })
            : []),
          ...(exportedThemes ? themePrintNotes(exportedThemes, t) : []),
        ]}
        theme={filter.exportTheme}
        // While a new filter loads, the screen still shows the previous
        // scope's numbers.
        disabled={
          objections.isPlaceholderData ||
          Boolean(exportedCq && questions.isPlaceholderData)
        }
      />
    ) : (
      <VoiceExportMenu unavailable={filter.exportUnavailable ?? 'pending'} />
    );

  return (
    <div className="space-y-4">
      <StreamFilterBar
        period={streamWindow.period}
        navigation={streamWindow.navigation}
        projectIds={streamWindow.projectIds}
        callCategory={streamWindow.callCategory}
        isLoading={fetching}
        onSearchChange={onSearchChange}
        extraActions={exportMenu}
      />

      <VoiceThemeFilterStrip
        filter={filter}
        // The echo that gives K is the questions' when they answered (their
        // blocks or not), else the stream's: «at least K» follows that
        // answer's cap — a questions answer without blocks has none.
        truncated={Boolean(
          questionsOn && questions.data ? cq?.truncated : objections.data?.truncated
        )}
        onSearchChange={onSearchChange}
      />

      <VoiceThemeFilterBlocks
        filter={filter}
        seriesWindow={tabSeriesWindow(streamWindow.dateRange, [
          objections,
          ...(questionsOn ? [questions] : []),
          ...(themesAvailable ? [themes] : []),
        ])}
        unnarrowed={themesBlock}
      >
        <VoiceLayers cards={cards} sections={sections} section={section} onOpen={open}>
          <TabsContent value="objections">
            <ObjectionsList
              stream={objections}
              search={search}
              onSearchChange={onSearchChange}
              limit={VOICE_TOP_ROWS}
              marked
            />
          </TabsContent>
          {questionsShown && (
            <>
              <TabsContent value="declines">
                {questionsBody((ready) => (
                  <VoiceSalesDeclines cq={ready} {...sectionProps} />
                ))}
              </TabsContent>
              <TabsContent value="questions">
                {questionsBody((ready) => (
                  <VoiceSalesQuestions cq={ready} {...sectionProps} />
                ))}
              </TabsContent>
            </>
          )}
          {themesBlock && <TabsContent value="themes">{themesBlock}</TabsContent>}
          <TabsContent value="changes">{changesBody()}</TabsContent>
        </VoiceLayers>
      </VoiceThemeFilterBlocks>
    </div>
  );
}
